import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { acquireAccessToken } from './ApiService';
import { msalInstance } from 'index';

import { Bookmark, AddBookmark, UpdateBookmark } from 'core/slices/models';

// Reusing the authentication base query from the example
// Note: The baseUrl is changed to /api/v2 for the new API
const rawBaseQuery = fetchBaseQuery({ baseUrl: '/api/v2' });

const authBaseQuery: typeof rawBaseQuery = async (args, api, extraOptions) => {
  let modifiedArgs: any = typeof args === 'string' ? { url: args } : { ...args };
  try {
    const token = await acquireAccessToken(msalInstance);
    const headers = new Headers((modifiedArgs.headers as any) || undefined);
    headers.set('Accept', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    modifiedArgs.headers = headers;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Bookmarks API token acquisition failed', e);
  }
  return rawBaseQuery(modifiedArgs, api, extraOptions);
};

export const bookmarksApiSlice = createApi({
  reducerPath: 'bookmarksApi',
  baseQuery: authBaseQuery,
  tagTypes: ['Bookmark'],
  endpoints: builder => ({
    // Assumed GET endpoint to fetch all bookmarks for a report, as it's common for CRUD APIs
    // getReportBookmarks: builder.query<Bookmark[], { projectKey: string; reportId: string }>(
    //   {
    //     query: ({ projectKey, reportId }) =>
    //       `projects/${projectKey}/reports/${reportId}/bookmarks`,
    //     providesTags: (result, error, { projectKey, reportId }) =>
    //       result
    //         ? [
    //           ...result.map(({ id }) => ({ type: 'Bookmark' as const, id })),
    //           { type: 'Bookmark', id: `${projectKey}-${reportId}-LIST` }, // Tag for the list of bookmarks for this report
    //         ]
    //         : [{ type: 'Bookmark', id: `${projectKey}-${reportId}-LIST` }], // Fallback for empty or error case
    //   },
    // ),
    createBookmark: builder.mutation<
      Bookmark,
      { projectKey: string; reportId: string; bookmark: AddBookmark }
    >({
      query: ({ projectKey, reportId, bookmark }) => ({
        url: `projects/${projectKey}/reports/${reportId}/bookmarks`,
        method: 'POST',
        body: bookmark,
      }),
      invalidatesTags: (result, error, { projectKey, reportId }) => [
        { type: 'Bookmark', id: `${projectKey}-${reportId}-LIST` }, // Invalidate the list of bookmarks for this report
      ],
    }),
    updateBookmark: builder.mutation<
      Bookmark,
      { projectKey: string; reportId: string; id: number; bookmark: UpdateBookmark }
    >({
      query: ({ projectKey, reportId, id, bookmark }) => ({
        url: `projects/${projectKey}/reports/${reportId}/bookmarks/${id}`,
        method: 'PUT',
        body: bookmark,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Bookmark', id }], // Invalidate the specific bookmark
    }),
    deleteBookmark: builder.mutation<
      void,
      { projectKey: string; reportId: string; id: number }
    >({
      query: ({ projectKey, reportId, id }) => ({
        url: `projects/${projectKey}/reports/${reportId}/bookmarks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id, projectKey, reportId }) => [
        { type: 'Bookmark', id }, // Invalidate the specific bookmark
        { type: 'Bookmark', id: `${projectKey}-${reportId}-LIST` }, // Invalidate the list of bookmarks for this report
      ],
    }),
  }),
});

export const {
  useCreateBookmarkMutation,
  useUpdateBookmarkMutation,
  useDeleteBookmarkMutation,
} = bookmarksApiSlice;
